import React from 'react';
import { BrowserRouter as Router, Switch, Redirect, Route } from 'react-router-dom';
import { ActivityIndicator, View } from 'react-native';
import { ApolloProvider } from '@apollo/react-hooks';

import color from '../style/color';
import { ApolloClient } from '../api/graphql/client';
import { getRegion, useRegionDetection } from '../lib/auth/region';
import { checkIsSignedIn } from '../lib/common/cognito';
import { getBrowserExtensionStoreUrl } from '../lib/common/browser';
import { useOauthRedirect } from '../lib/auth/oauthRedirect';
import LandingNavigator from './landing/LandingNavigator';
import AuthNavigator from './auth/AuthNavigator';
import SignInSucceededPage from '../pages/auth/SignInSucceededPage';
import OnboardingNavigator from './onboarding/OnboardingNavigator';
import HomeNavigator from './home/HomeNavigator';
import TrackingModal from '../components/common/TrackingModal';
import BrowserExtensionAutoLoginPage from '../pages/auth/BrowserExtensionAutoLoginPage';
import OnlineOfferActivationPage from '../pages/public/OnlineOfferActivationPage';
import IOSDonationPage from '../pages/public/IOSDonationPage';
import IosAutoDonationConfirmationPage from '../pages/public/IosAutoDonationConfirmationPage';
import FloaIframePage from '../pages/public/FloaIframePage';
import MobileSafariExtensionInstallationPage from '../pages/public/MobileSafariExtensionInstallationPage';
import FloaPaymentPageWithUrlParams from '../pages/public/FloaPaymentPageWithUrlParams';
import GiftCardPaymentPage from '../pages/public/GiftCardPaymentPage';
import GiftCardPaymentV2Page from '../pages/public/GiftCardPaymentV2Page';
import GiftCardPaymentVerifyPage from '../pages/public/GiftCardPaymentVerifyPage';
import GiftCardPaymentSuccessPage from '../pages/public/GiftCardPaymentSuccessPage';
import GiftCardPaymentFailurePage from '../pages/public/GiftCardPaymentFailurePage';
import ProductDiscoveryPage from '../pages/products/ProductDiscoveryPage';
import OnlineBackgroundOfferActivationPage from '../pages/public/OnlineBackgroundOfferActivationPage';
import DeeplinkRedirectPage from '../pages/public/DeeplinkRedirectPage';

function RootNavigator({ apolloClient }: { apolloClient: ApolloClient | undefined }) {
    return (
        <Router>
            <RootNavigatorRoutes {...{ apolloClient }} />
        </Router>
    );
}

function RootNavigatorRoutes({ apolloClient }: { apolloClient: ApolloClient | undefined }) {
    const isRegionLoading = useRegionDetection(apolloClient);
    useOauthRedirect();
    if (isRegionLoading) return <LoadingPage />;
    return (
        <View style={{ flex: 1 }}>
            <Switch>
                <Route
                    path="/landing"
                    render={() => {
                        if (!checkIsSignedIn()) {
                            if (apolloClient)
                                return (
                                    <ApolloProvider client={apolloClient}>
                                        <LandingNavigator />
                                        <TrackingModal />
                                    </ApolloProvider>
                                );
                            return null;
                        } else return <Redirect to={{ pathname: '/home', search: window.location.search }} />;
                    }}
                />
                <Route
                    path="/auth"
                    render={() => {
                        if (!checkIsSignedIn())
                            return (
                                <>
                                    <AuthNavigator />
                                    {apolloClient ? (
                                        <ApolloProvider client={apolloClient}>
                                            <TrackingModal />
                                        </ApolloProvider>
                                    ) : null}
                                </>
                            );
                        else return <Redirect to={{ pathname: '/home', search: window.location.search }} />;
                    }}
                />
                <Route
                    path="/onboarding"
                    render={() => {
                        if (checkIsSignedIn()) {
                            if (apolloClient)
                                return (
                                    <ApolloProvider client={apolloClient}>
                                        <OnboardingNavigator />
                                        <TrackingModal />
                                    </ApolloProvider>
                                );
                            return null;
                        } else return <Redirect to={{ pathname: '/auth', search: window.location.search }} />;
                    }}
                />
                <Route
                    path="/home"
                    render={() => {
                        if (checkIsSignedIn()) {
                            if (apolloClient)
                                return (
                                    <ApolloProvider client={apolloClient}>
                                        <HomeNavigator />
                                        <TrackingModal />
                                    </ApolloProvider>
                                );
                            return null;
                        } else return <Redirect to={{ pathname: '/auth', search: window.location.search }} />;
                    }}
                />
                <Route
                    path="/browser-extension"
                    exact={true}
                    render={() => {
                        const region = getRegion();
                        const browserExtensionStoreUrl = getBrowserExtensionStoreUrl(region);
                        window.location.replace(browserExtensionStoreUrl);
                        return null;
                    }}
                />
                <Route path="/browser-extension/auto-login" render={() => <BrowserExtensionAutoLoginPage />} />
                <Route
                    path="/online-offer-activation/:userId/:offerId/:stackMode?"
                    render={() => {
                        return <OnlineOfferActivationPage />;
                    }}
                />
                <Route
                    path="/online-background-offer-activation/:userId/:offerId/:stackMode?"
                    render={() => {
                        return <OnlineBackgroundOfferActivationPage />;
                    }}
                />
                <Route
                    path="/sign-in-succeeded"
                    render={() => {
                        return <SignInSucceededPage />;
                    }}
                />
                <Route
                    path="/redeem-ios-donation/:userId/:token/:rewardId/:rewardTitle/:rewardValue/:rewardLogoUrl/:stackMode?"
                    render={() => {
                        return <IOSDonationPage />;
                    }}
                />
                <Route
                    path="/ios-auto-donation-confirmation/:userId/:token/:rewardId/:rewardTitle/:rewardLogoUrl/:stackMode?"
                    render={() => {
                        return <IosAutoDonationConfirmationPage />;
                    }}
                />
                <Route
                    path="/floa-iframe"
                    render={() => {
                        return <FloaIframePage />;
                    }}
                />
                <Route
                    path="/floa-payment-page-with-url-params"
                    render={() => {
                        return <FloaPaymentPageWithUrlParams />;
                    }}
                />
                <Route path="/gift-card-payment/verify" render={() => <GiftCardPaymentVerifyPage />} />
                <Route path="/gift-card-payment/success" render={() => <GiftCardPaymentSuccessPage />} />
                <Route path="/gift-card-payment/failure" render={() => <GiftCardPaymentFailurePage />} />
                <Route path="/gift-card-payment-v2" exact={true} render={() => <GiftCardPaymentV2Page />} />
                <Route
                    path="/gift-card-payment"
                    exact={true}
                    render={() => {
                        if (!apolloClient) return <LoadingPage />;
                        return (
                            <ApolloProvider client={apolloClient}>
                                <GiftCardPaymentPage />
                            </ApolloProvider>
                        );
                    }}
                />
                <Route
                    path="/mobile-safari-extension-installation"
                    render={() => {
                        return <MobileSafariExtensionInstallationPage />;
                    }}
                />
                <Route
                    path={['/ai/:conversationId?/:urlParams?', '/product-discovery/:conversationId?/:urlParams?']} // We keep the old route for backward compatibility when redirecting from the extension
                    render={() => {
                        if (checkIsSignedIn()) {
                            if (apolloClient)
                                return (
                                    <ApolloProvider client={apolloClient}>
                                        <ProductDiscoveryPage />
                                    </ApolloProvider>
                                );
                            return null;
                        }
                        // If the user is unlogged, we show a loading component and wait for the automatic log-in handled by extension. We add a timeout to avoid infinite loading.
                        else return <LoadingPage timeoutInMs={5000} />;
                    }}
                />
                <Route
                    path="/deeplink"
                    render={() => {
                        return <DeeplinkRedirectPage />;
                    }}
                />
                <Route path="*">
                    <Redirect to={{ pathname: '/landing', search: window.location.search }} />
                </Route>
            </Switch>
        </View>
    );
}

function LoadingPage({ timeoutInMs }: { timeoutInMs?: number }) {
    const [shouldRedirectToAuth, setShouldRedirectToAuth] = React.useState(false);
    React.useEffect(() => {
        if (timeoutInMs) {
            const timeoutId = setTimeout(() => {
                setShouldRedirectToAuth(true);
            }, timeoutInMs);
            return () => clearTimeout(timeoutId);
        }
    }, [timeoutInMs]);
    if (shouldRedirectToAuth) return <Redirect to={{ pathname: '/auth', search: window.location.search }} />;
    return (
        <View style={{ height: '100vh', justifyContent: 'center' }}>
            <ActivityIndicator size="large" color={color.alto} />
        </View>
    );
}

export default RootNavigator;
